"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import {
  IconCalendarQuestion,
  IconCalendarX,
  IconClipboardText,
  IconFlask,
  IconListCheck,
  IconMapPin,
  IconPackage,
  IconPlaneArrival,
  IconPlaneDeparture,
  IconReceipt,
  IconTicket,
} from "@tabler/icons-react"
import { useTranslations } from "next-intl"
import { useFormatter } from "@/hooks/use-formatter"

import { usePermissions } from "@/hooks/use-permissions"
import { useAuthStore } from "@/stores/auth-store"

import api from "@/lib/api"

/** `yyyy-MM-dd` → local midnight. `new Date("2026-07-30")` would parse as UTC. */
export function parseLocalDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (!match) return null
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return Number.isNaN(date.getTime()) ? null : date
}

function startOfToday(): Date {
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  return now
}

const DAY_MS = 86_400_000

/**
 * Booking-status filter. Static literal keys (T-72-01: never t(someVar)).
 * "pending" is the default — the board is a worklist first, and showing booked
 * patients is the deliberate widening.
 */
export const BOOKING_FILTERS = [
  { key: "pending", labelKey: "frontdeskBooking.booking.pending" },
  { key: "booked", labelKey: "frontdeskBooking.booking.booked" },
  { key: "all", labelKey: "frontdeskBooking.booking.all" },
] as const

/**
 * The logistics desk's status filter (Radiance 261001), in place of the
 * booking statuses: it books nothing, it owes each trip a ride and a flight
 * check-in. "All" is the default — done rows stay on the board, quiet.
 */
export const NEED_FILTERS = [
  { key: "all", labelKey: "frontdeskBooking.need.all" },
  { key: "check_in", labelKey: "frontdeskBooking.need.checkIn" },
  { key: "ride", labelKey: "frontdeskBooking.need.ride" },
  { key: "both", labelKey: "frontdeskBooking.need.both" },
  // Offered only where the clinic tracks IDs (identity_documents_enabled) —
  // see `showIdentity` on the hook.
  { key: "id", labelKey: "frontdeskBooking.need.id" },
] as const

export type Need = (typeof NEED_FILTERS)[number]["key"]

/** Range options for the days filter. Must stay inside the API's 1..90 clamp. */
export const DAY_RANGES = [1, 3, 7, 14, 30] as const
export const DEFAULT_RANGE = 7

/**
 * ArtDent 261015 — the range filter's "custom" choice: an explicit window,
 * both days inclusive, as `yyyy-MM-dd`. Sent to the server as `from`/`to` in
 * place of `days`; the server then bounds every dated section on BOTH ends
 * (a desk searching dates wants those dates, overdue rows included only when
 * they fall inside).
 */
export interface DateWindow {
  from: string
  to: string
}

/**
 * Radiance 261005 — a flight landing at this time or later leaves no room for
 * a visit the same day: the logistics desk is told to leave the first
 * appointment for the next day. Clinic-local "HH:MM", as the trip form stores it.
 */
export const LATE_ARRIVAL_FROM = "15:30"

/** Does an arrival at this "HH:MM" land too late for a visit that day? No time, no answer. */
export function landsLate(time: string | null | undefined): boolean {
  const match = time ? /^(\d{1,2}):(\d{2})/.exec(time) : null
  if (!match) return false
  const [cutH, cutM] = LATE_ARRIVAL_FROM.split(":").map(Number)
  return Number(match[1]) * 60 + Number(match[2]) >= cutH * 60 + cutM
}

/* ────────────────────────────── payload shapes ─────────────────────────── */

/** An amount of money in one currency, as the board prints it. */
export interface DepositAmount {
  amount: number
  currency: string
}

interface BaseRow {
  /** Desk board: does the patient have an agreed plan (an accepted quote)? */
  has_agreed_plan?: boolean
  /**
   * Radiance 261005 — the Kapar left: deposits no invoice has taken yet, one
   * entry per currency. Absent where the clinic does not track it.
   */
  deposits?: DepositAmount[]
  lead_id: string
  patient_name: string
  /** Present only when the board asked for booked rows; absent means unbooked. */
  booked?: boolean
  /**
   * Phase 82 — the patient's care team, stamped onto every row by
   * BookingWorklistService::withCareTeams(). The whole BLOCK is absent when the
   * clinic has not opted in, which is how the board knows to drop the column;
   * an opted-in clinic always gets the block, with nulls for empty slots.
   */
  care_team?: {
    doctor_id: number | null
    prosthetist_id: number | null
    doctor: string | null
    prosthetist: string | null
  }
}

interface TravelRow extends BaseRow {
  travel_id: string
  arrival_date?: string | null
  arrival_airport?: string | null
  arrival_airline?: string | null
  departure_date?: string | null
  departure_airport?: string | null
  departure_airline?: string | null
  hotel_name?: string | null
  /**
   * Which visit this trip IS for the patient — 1 for a first-timer, 2 for a
   * returner, and so on. Derived server-side from the trip's mirrored
   * ClinicalVisit; null when the clinic has no visit categorization, in which
   * case the arrival keeps its plain colour.
   */
  visit_ordinal?: number | null
  /** Desk board (260923): arrivals carry their airport pickup and the visit half. */
  arrival_time?: string | null
  visit_booked?: boolean
  drive?: DriveSummary | null
  /** Radiance 261001 — the patient's check-in for this row's flight. */
  flight_check_in?: FlightCheckInSummary
  /** Radiance 261001 — ID card or passport on file; null when the clinic does not track it. */
  identity_on_file?: boolean | null
}

/** Whether the patient is checked in for a flight, and when check-in opens (24h before it). */
interface FlightCheckInSummary {
  checked_in: boolean
  checked_in_at: string | null
  opens_at: string | null
}

/** A ride the desk dispatches — the trip's airport pickup or drop-off. */
interface DriveSummary {
  transfer_id: string
  status: string
  driver_id: string | null
  driver_name: string | null
  pickup_date: string | null
  pickup_time: string | null
}

interface LabRow extends BaseRow {
  lab_order_id: string
  order_number: string
  material: string | null
  /** ArtDent 261007 — every material on the order, main first. */
  materials?: string[]
  /** ISO moment the desk should act by — the estimate minus 24h. */
  act_by: string
  phase: {
    name: string
    index: number
    total: number
    /** Null on an undated resin try-in (ArtDent 261007). */
    due_date: string | null
    due_time: string | null
    /** ArtDent 261007 — the doctor's resin try-in: book it, whatever its date. */
    tryin?: boolean
    round?: number
  }
}

interface InvoiceRow extends BaseRow {
  invoice_id: string
  invoice_number: string
  status: string
  dated: string | null
}

interface TaskRow extends BaseRow {
  task_id: string
  title: string
  requested_by: string | null
  due_at: string | null
  requested_at: string | null
}

/**
 * ArtDent 261015 — a follow-up a doctor asked for from the chart or the
 * treatment plan (PatientFollowup): the other source of Doctor rows. Sent
 * under booking_board_followups_enabled, empty otherwise.
 */
interface FollowupRow extends BaseRow {
  followup_id: string
  target_date: string | null
  note: string | null
  requested_at: string | null
  requested_by?: string | null
}

/**
 * Dentour 260923 — a departure is actioned by its airport drive. Without
 * drives (261008) it is actioned by a visit before the flight, and the two
 * flags say what is still open on top of that.
 */
interface DepartureRow extends TravelRow {
  departure_time?: string | null
  open_lab_work?: boolean
  pending_stage?: boolean
}

/** Dentour 261008 — already landed, nothing booked for the rest of the trip. */
interface LandedRow extends TravelRow {
  departure_time?: string | null
}

interface StageRow extends BaseRow {
  treatment_id: string
  treatment_title: string | null
  stage_id: string
  stage_name: string
  planned_date?: string | null
  /** Phase 89 — the doctor assigned to the treatment; Book prefills them. */
  treatment_doctor_id?: number | null
}

interface MissedRow extends BaseRow {
  appointment_id: string
  status: "cancelled" | "no_show" | string
  missed_at: string | null
}

interface DeliveredRow extends BaseRow {
  lab_order_id: string
  order_number: string
  material: string | null
  /** ArtDent 261007 — every material on the order, main first. */
  materials?: string[]
  delivered_at: string | null
}

/**
 * Radiance 261005 — a logistics-desk patient with no trip yet (accepted quote
 * or Kapar): the row's action is Add trip.
 */
interface NoTripRow extends BaseRow {
  /** Which door they came in by — the quote wins when there are both. */
  joined_by: "quote" | "deposit"
  /** When: the quote's acceptance or the first payment (Y-m-d). */
  since: string | null
}

interface WorklistData {
  arrivals: TravelRow[]
  landed: LandedRow[]
  departures: DepartureRow[]
  no_trip: NoTripRow[]
  lab_bookings: LabRow[]
  invoice_bookings: InvoiceRow[]
  appointment_tasks: TaskRow[]
  stage_bookings: StageRow[]
  undated_stages: StageRow[]
  missed_visits: MissedRow[]
  delivered_labs: DeliveredRow[]
  followups: FollowupRow[]
}

const EMPTY: WorklistData = {
  arrivals: [],
  landed: [],
  departures: [],
  no_trip: [],
  lab_bookings: [],
  invoice_bookings: [],
  appointment_tasks: [],
  stage_bookings: [],
  undated_stages: [],
  missed_visits: [],
  delivered_labs: [],
  followups: [],
}

interface WorklistLoad {
  data: WorklistData
  /** The reasons whose sections the server sent — see {@link reasonsFor}. */
  reasons: Reason[]
  /**
   * The logistics desk's board (Radiance 261001): it books no visits, so its
   * rows offer the flight check-in instead of Book. Decided by the server from
   * the caller's grants, the same call that narrows the sections.
   */
  checkInMode: boolean
}

/** Never throws — a failed load degrades to the positive empty state. */
async function fetchWorklist(
  days: number,
  includeBooked: boolean,
  range: DateWindow | null,
): Promise<WorklistLoad> {
  try {
    // A custom window replaces `days` outright — the server would let the
    // window win anyway, but sending both would read as if `days` mattered.
    const window = range ? { from: range.from, to: range.to } : { days }
    const response = await api.get<{
      data: Partial<WorklistData>
      meta?: { travelling_only?: boolean }
    }>(
      "/booking-worklist",
      // `board` selects the front desk's own five sections. Without it the
      // endpoint returns the coordinator's set, which this table cannot read.
      {
        params: includeBooked
          ? { ...window, include_booked: 1, board: "frontdesk" }
          : { ...window, board: "frontdesk" },
      },
    )
    const sent = response.data.data ?? {}
    return {
      data: { ...EMPTY, ...sent },
      reasons: reasonsFor(Object.keys(sent)),
      checkInMode: response.data.meta?.travelling_only === true,
    }
  } catch {
    return { data: EMPTY, reasons: REASON_ORDER, checkInMode: false }
  }
}

/* ──────────────────────────────── reasons ──────────────────────────────── */

/**
 * Why a patient is on the board. This is the column the whole table exists to
 * answer — one glance should say whether someone is here because they are
 * landing, flying out, waiting on the lab, or owing money.
 */
export type Reason =
  | "arriving"
  | "landed"
  | "departing"
  | "no_trip"
  | "stage"
  | "undated"
  | "lab"
  | "delivered"
  | "invoice"
  | "missed"
  | "task"

export const REASON_ORDER: Reason[] = [
  "arriving",
  "landed",
  "departing",
  "no_trip",
  "stage",
  "undated",
  "lab",
  "delivered",
  "invoice",
  "missed",
  "task",
]

/** The reason each section of the payload feeds. */
const SECTION_REASON: Record<keyof WorklistData, Reason> = {
  arrivals: "arriving",
  departures: "departing",
  landed: "landed",
  no_trip: "no_trip",
  lab_bookings: "lab",
  invoice_bookings: "invoice",
  appointment_tasks: "task",
  stage_bookings: "stage",
  undated_stages: "undated",
  missed_visits: "missed",
  delivered_labs: "delivered",
  followups: "task",
}

/**
 * The reasons this board offers, in board order: only those whose section
 * the server actually sent. The server decides the slice from the caller's
 * grants — Radiance's logistics coordinator (261001) gets arrivals and
 * departures alone — so a filter or legend entry for a reason that can never
 * hold a row would only be noise. An empty payload keeps every reason.
 */
function reasonsFor(sections: string[]): Reason[] {
  const sent = new Set(
    sections.map((key) => SECTION_REASON[key as keyof WorklistData]).filter(Boolean),
  )
  return sent.size === 0 ? REASON_ORDER : REASON_ORDER.filter((reason) => sent.has(reason))
}

/** Static literal keys per reason (T-72-01: never t(someVar)). */
export const REASON_LABEL_KEY: Record<Reason, string> = {
  arriving: "frontdeskBooking.reason.arriving",
  landed: "frontdeskBooking.reason.landed",
  departing: "frontdeskBooking.reason.departing",
  no_trip: "frontdeskBooking.reason.noTrip",
  stage: "frontdeskBooking.reason.stage",
  undated: "frontdeskBooking.reason.undated",
  lab: "frontdeskBooking.reason.lab",
  delivered: "frontdeskBooking.reason.delivered",
  invoice: "frontdeskBooking.reason.invoice",
  missed: "frontdeskBooking.reason.missed",
  task: "frontdeskBooking.reason.task",
}

/**
 * Reason → icon. A plane leaving and a plane landing are legible at a glance in
 * a way two differently-coloured pills never are, so the icon carries the
 * meaning and the colour only reinforces it.
 */
export const REASON_ICON: Record<Reason, typeof IconPlaneArrival> = {
  arriving: IconPlaneArrival,
  landed: IconMapPin,
  departing: IconPlaneDeparture,
  no_trip: IconTicket,
  stage: IconListCheck,
  undated: IconCalendarQuestion,
  lab: IconFlask,
  delivered: IconPackage,
  invoice: IconReceipt,
  missed: IconCalendarX,
  task: IconClipboardText,
}

/** Reason → icon colour. Static map; never interpolated into a class name. */
export const REASON_TONE: Record<Reason, string> = {
  arriving: "text-emerald-600 dark:text-emerald-400",
  landed: "text-blue-600 dark:text-blue-400",
  departing: "text-orange-600 dark:text-orange-400",
  no_trip: "text-indigo-600 dark:text-indigo-400",
  stage: "text-teal-600 dark:text-teal-400",
  undated: "text-amber-600 dark:text-amber-400",
  lab: "text-violet-600 dark:text-violet-400",
  delivered: "text-fuchsia-600 dark:text-fuchsia-400",
  invoice: "text-sky-600 dark:text-sky-500",
  missed: "text-red-600 dark:text-red-400",
  task: "text-rose-600 dark:text-rose-400",
}

/**
 * Arriving plane → colour by which visit this is for the patient (Dentour
 * 260826). A first-timer is a different phone call from a third-time returner,
 * and the desk wants to know which before dialling.
 *
 *   1st visit  black         — never been here; set up from scratch
 *   2nd visit  green         — treated once and coming back
 *   3rd visit  purple        — a returner
 *   4th+       darker purple — an established regular
 *
 * Static map, index 1..4 (T-72-01 posture: never interpolated into a class
 * name). The 4th+ shade deepens the 3rd's purple rather than introducing a new
 * hue, so the two read as one "returning regular" family that intensifies.
 *
 * In DARK mode "deeper" inverts, exactly as every other tone here inverts
 * 600 → 400: on a dark ground the stronger signal is the brighter one, so 4th+
 * goes 900 → 300 while 3rd stays 600 → 400.
 *
 * Colour alone is never the only signal: {@link visitLabelKeyFor} puts the same
 * fact on the icon's accessible label.
 */
export const ARRIVAL_VISIT_TONE: Record<1 | 2 | 3 | 4, string> = {
  1: "text-neutral-900 dark:text-neutral-100",
  2: "text-emerald-600 dark:text-emerald-400",
  3: "text-purple-600 dark:text-purple-400",
  4: "text-purple-900 dark:text-purple-300",
}

/** Static literal keys per visit bucket (T-72-01: never t(someVar)). */
export const ARRIVAL_VISIT_LABEL_KEY: Record<1 | 2 | 3 | 4, string> = {
  1: "frontdeskBooking.visit.first",
  2: "frontdeskBooking.visit.second",
  3: "frontdeskBooking.visit.third",
  4: "frontdeskBooking.visit.fourthPlus",
}

/**
 * Dentour 261015 — on a clinic whose planes wear visit colours
 * (`appointments.calendar.travel_visit_colors`) the plane sits in a small
 * swatch badge instead of being tinted: WHITE for a first visit (a white
 * stroke on the table would vanish, the badge gives it a ring), GREEN for the
 * second, BROWN for a returning patient — third and later share it. The same
 * three colours as the calendar's planes.
 */
export const ARRIVAL_VISIT_SWATCH: Record<1 | 2 | 3 | 4, string> = {
  1: "bg-white text-neutral-900 ring-1 ring-neutral-300 dark:bg-white dark:text-neutral-900 dark:ring-neutral-400",
  2: "bg-green-600 text-white",
  3: "bg-amber-900 text-white",
  4: "bg-amber-900 text-white",
}

/** 1/2/3/4+ bucket for a row, or null when the ordinal is unknown. */
export function visitBucketFor(row: UnifiedRow): 1 | 2 | 3 | 4 | null {
  return visitBucket(row)
}

function visitBucket(row: UnifiedRow): 1 | 2 | 3 | 4 | null {
  if (row.reason !== "arriving" || row.visitOrdinal === null) return null
  if (row.visitOrdinal <= 1) return 1
  if (row.visitOrdinal === 2) return 2
  if (row.visitOrdinal === 3) return 3
  return 4
}

/**
 * Icon colour for a row: arrivals with a known visit number are coloured by it,
 * everything else keeps its reason colour.
 */
export function toneFor(row: UnifiedRow): string {
  const bucket = visitBucket(row)
  return bucket === null ? REASON_TONE[row.reason] : ARRIVAL_VISIT_TONE[bucket]
}

/** Accessible label key for the coloured plane, or null when it carries none. */
export function visitLabelKeyFor(row: UnifiedRow): string | null {
  const bucket = visitBucket(row)
  return bucket === null ? null : ARRIVAL_VISIT_LABEL_KEY[bucket]
}

/* ──────────────────────────────── urgency ──────────────────────────────── */

/**
 * How urgent a dated row is, as one scale every reason shares so a colour means
 * the same thing wherever the desk sees it. Boundaries are the desk's own
 * vocabulary: past its date is already a problem, today/tomorrow is too close
 * to arrange anything, two-to-three days still fits a booking comfortably.
 */
export type Urgency = "late" | "today" | "close" | "near" | "later"

function urgencyOf(diffDays: number): Urgency {
  if (diffDays < 0) return "late"
  if (diffDays === 0) return "today"
  if (diffDays === 1) return "close"
  if (diffDays <= 3) return "near"
  return "later"
}

export const URGENCY_RAIL: Record<Urgency, string> = {
  late: "bg-red-500",
  today: "bg-orange-500",
  close: "bg-amber-500",
  near: "bg-sky-500",
  later: "bg-border",
}

export const URGENCY_TEXT: Record<Urgency, string> = {
  late: "font-medium text-red-600 dark:text-red-400",
  today: "font-medium text-orange-600 dark:text-orange-400",
  close: "text-amber-600 dark:text-amber-500",
  near: "text-text-dim",
  later: "text-text-dim",
}

// Static literal-key map (T-73-05: never t(materialVar)) — resolved against the
// existing `labOrders` namespace so the copy stays in one place.
const MATERIAL_KEY: Record<string, string> = {
  metal_ceramic: "material.metalCeramic",
  laser_sinter: "material.laserSinter",
  emax: "material.emax",
  implant: "material.implant",
  zirconia: "material.zirconia",
  inlay_onlay: "material.inlayOnlay",
  acetal: "material.acetal",
}

/** One flattened row: a patient, one reason to call them, one date. */
export interface UnifiedRow {
  id: string
  leadId: string
  patientName: string
  reason: Reason
  detail: string
  date: Date | null
  diffDays: number | null
  urgency: Urgency
  booked: boolean
  /** Arrivals only: which visit this is for the patient. Null when unknown. */
  visitOrdinal: number | null
  /**
   * Arrivals and departures (Dentour 260923): the trip's airport ride, which the
   * Driver column dispatches — pickup on arrival, drop-off on departure.
   */
  ride?: {
    kind: "arrival_pickup" | "departure_dropoff"
    travelId: string
    date: string | null
    time: string | null
    from: string | null
    to: string | null
    drive: {
      transferId: string
      driverId: string | null
      driverName: string | null
      pickupDate: string | null
      pickupTime: string | null
    } | null
  }
  /** Arrivals on the desk board: is the visit half done (a visit on/after landing)? */
  visitBooked?: boolean
  /**
   * Radiance 261001 — arrivals and departures: the patient's check-in for this
   * row's flight, which the logistics desk sets where Book would be.
   */
  checkIn?: {
    travelId: string
    leg: "arrival" | "departure"
    checkedIn: boolean
    /** When online check-in opens — 24 hours before the flight. */
    opensAt: Date | null
  }
  /**
   * Radiance 261001 — arrivals and departures: is the patient's ID card or
   * passport on file? Null when the clinic does not track it (no marker).
   */
  identityOnFile?: boolean | null
  /**
   * Step rows (planned and undated): the treatment step itself. Book sends it
   * with the new appointment, which links the step — the only thing that
   * clears a step row.
   */
  stageId?: string
  /** Phase 89 — the doctor the step's treatment is assigned to (prefill on Book). */
  stageDoctorId?: number | null
  /**
   * ArtDent 261015 — a doctor's follow-up request (Doctor rows from the
   * chart): the row offers "handled, no visit" as well as Book.
   */
  followupId?: string
  /** Dentour 260923 — the patient has an Agreed plan the row can open. */
  hasAgreedPlan: boolean
  /** Radiance 261005 — the Kapar left on the patient; empty when none. */
  deposits: DepositAmount[]
  /**
   * Radiance 261005 — an arrival landing at {@link LATE_ARRIVAL_FROM} or
   * later: the first appointment belongs on the next day.
   */
  visitNextDay?: boolean
  /** Phase 82 — the patient's care team; null when the clinic has not opted in. */
  careTeam: {
    doctorId: number | null
    prosthetistId: number | null
    doctorName: string | null
    prosthetistName: string | null
  } | null
}

/**
 * Front-desk "Needs booking" board (Dentour 260818).
 *
 * ONE table for the desk's whole queue, because the desk does not think in
 * sources — it thinks "who do I have to call, and by when". Arrivals,
 * departures, lab work coming back, paid invoices, unpaid invoices, planned
 * stages and doctor follow-ups all land in the same list, ordered by the day
 * the action is needed, most urgent first.
 *
 * Every row carries its REASON as a coloured chip, and the chip row above the
 * table doubles as the filter: one chip per reason with its live count, plus a
 * day-range control. Between them, "why is this person here?" is answerable at
 * a glance and narrowable in one click.
 *
 * The day range is sent to the SERVER (`?days=`), not applied here. Three of
 * the seven sections have no upper date bound of their own, so filtering in the
 * browser would silently hide rows the server never sent, and the counts on the
 * chips would lie. Omitting the param preserves the endpoint's original
 * behaviour, which is what the coordinator's widget still relies on.
 *
 * Gating mirrors {@link import('../coordinator/booking-worklist').BookingWorklist}
 * exactly: nothing is requested until `usePermissions().isLoaded`, and without
 * the flag + grant the board does not exist (hide-not-disable).
 *
 * Rows deep-link to the PATIENT, never to `/lab-orders/{id}` or `/invoices/{id}`:
 * front desk holds `leads.view.all`, so the patient page is the one target that
 * works for every reason here.
 */

/**
 * The front-desk board's whole state: what to ask the server for, what came
 * back, and the rows that fall out of it.
 *
 * Lifted out of the table because the FILTERS live in the page header now and
 * the ROWS live in the panel below it — two sibling surfaces that must agree on
 * one set of counts. Threading that through props from one to the other would
 * make the header depend on the table; a hook lets the page own it and hand
 * each half what it needs.
 */
/**
 * What a logistics row still owes (Radiance 261001): the patient's flight
 * check-in, a driver on its airport ride — a trip with no ride yet needs one
 * just as much as a ride with nobody on it — and, where the clinic tracks it,
 * the ID card or passport the check-in needs.
 */
export function needsOf(row: UnifiedRow): { checkIn: boolean; ride: boolean; id: boolean } {
  return {
    checkIn: row.checkIn !== undefined && !row.checkIn.checkedIn,
    ride: row.ride !== undefined && !row.ride.drive?.driverId,
    id: row.identityOnFile === false,
  }
}

function matchesNeed(row: UnifiedRow, need: Need): boolean {
  const owed = needsOf(row)
  if (need === "check_in") return owed.checkIn
  if (need === "ride") return owed.ride
  if (need === "both") return owed.checkIn && owed.ride
  if (need === "id") return owed.id
  return true
}

export function useNeedsBooking({
  refreshKey = 0,
  onAppointmentsChanged,
}: {
  /**
   * An outside refresh signal. Bumping it re-runs the load, exactly like the
   * refresh button — the coordinator dashboard bumps it when an appointment
   * changes in Today's Schedule, so the board never shows a row that panel
   * just resolved (or hides one it just reopened).
   */
  refreshKey?: number
  /**
   * Called after the board itself books an appointment, INSTEAD of its own
   * refresh — the caller is expected to bump `refreshKey`, which reloads this
   * board and every sibling surface together. Absent (the front-desk page),
   * the board simply refreshes itself.
   */
  onAppointmentsChanged?: () => void
} = {}) {
  const t = useTranslations("dashboard")
  const tLab = useTranslations("labOrders")
  const formatter = useFormatter()
  const { isLoaded, can } = usePermissions()
  // 🟥 EITHER FLAG (260907), mirroring the OR-gate the route now carries. This
  // board belongs to two features: the care-team workflow it was built inside,
  // and the desk board itself. Gating on `care_team_enabled` alone forced a
  // clinic that wanted only the table to switch on the whole Care Team feature
  // — a card on every patient page — to unlock it. ArtDent runs the board and
  // no care teams; Dentour runs both and is unaffected.
  //
  // The care-team COLUMN is not gated here at all: `showCareTeam` below is
  // derived from the rows, so a clinic whose payload carries no care-team block
  // never renders it. One less flag read, and it cannot disagree with the data.
  //
  // ⚠ ONE selector, not two ORed inline: `a() || b()` short-circuits, which
  // makes the second useAuthStore a CONDITIONAL hook call and breaks the rules
  // of hooks the moment the first flag is on (eslint catches it; React would
  // mis-order the hook list at runtime).
  const flagOn = useAuthStore(
    (s) =>
      (s.user?.feature_flags?.care_team_enabled ?? false) ||
      (s.user?.feature_flags?.frontdesk_dashboard_enabled ?? false),
  )

  const [data, setData] = useState<WorklistData>(EMPTY)
  // The reasons whose section the server sent (Radiance 261001); the
  // flag rules below narrow them further.
  const [serverReasons, setReasons] = useState<Reason[]>(REASON_ORDER)
  // The server says which board this is (meta.travelling_only). Until its
  // first answer, predict it from the same grants it decides on, so the page
  // header does not flash the desk's booking numbers at the logistics desk.
  const [serverCheckInMode, setServerCheckInMode] = useState<boolean | null>(null)
  const checkInMode =
    serverCheckInMode ?? (isLoaded && !can("leads.view.all") && can("leads.view.travelling"))
  const [need, setNeed] = useState<Need>("all")
  const [isLoading, setIsLoading] = useState(true)
  const [days, setDaysState] = useState<number>(DEFAULT_RANGE)
  /**
   * The custom window, when the desk picked one instead of a `days` preset.
   * Exclusive with `days` on the wire: picking a preset clears it, picking a
   * window leaves `days` as the preset to fall back to on "clear".
   */
  const [range, setRange] = useState<DateWindow | null>(null)
  const setDays = useCallback((next: number) => {
    setRange(null)
    setDaysState(next)
  }, [])
  const [reason, setReason] = useState<Reason | "all">("all")
  /**
   * "pending" is the board's default and the strict worklist: only patients
   * still waiting on the desk. The other two ask the server for booked rows as
   * well — "booked" then narrows to them here, so switching between the two
   * costs no extra request.
   */
  const [booking, setBooking] = useState<"pending" | "booked" | "all">("pending")
  /** Bumped by the refresh button to re-run the load effect. */
  const [reloadKey, setReloadKey] = useState(0)

  const allowed = isLoaded && flagOn && can("appointments.create")

  useEffect(() => {
    if (!allowed) return

    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      try {
        const result = await fetchWorklist(days, booking !== "pending", range)
        if (!cancelled) {
          setData(result.data)
          setReasons(result.reasons)
          setServerCheckInMode(result.checkInMode)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()

    return () => {
      cancelled = true
    }
  }, [allowed, days, range, booking, reloadKey, refreshKey])

  /** The custom window as the filter and the header print it: "12 Oct – 20 Oct". */
  const rangeLabel = useMemo((): string | null => {
    if (!range) return null
    const from = parseLocalDate(range.from)
    const to = parseLocalDate(range.to)
    if (!from || !to) return null
    const day = (d: Date) => formatter.dateTime(d, { day: "numeric", month: "short" })
    return `${day(from)} – ${day(to)}`
  }, [range, formatter])

  const shortDate = useCallback(
    (date: Date): string => formatter.dateTime(date, { day: "numeric", month: "short" }),
    [formatter],
  )

  const rows = useMemo<UnifiedRow[]>(() => {
    const today = startOfToday()

    const build = (
      id: string,
      row: BaseRow,
      r: Reason,
      detail: string,
      rawDate: string | null | undefined,
      visitOrdinal: number | null = null,
    ): UnifiedRow => {
      const date = rawDate ? parseLocalDate(rawDate) ?? new Date(rawDate) : null
      const valid = date && !Number.isNaN(date.getTime()) ? date : null
      const diffDays = valid
        ? Math.round((valid.getTime() - today.getTime()) / DAY_MS)
        : null
      return {
        id,
        leadId: row.lead_id,
        patientName: row.patient_name || "—",
        reason: r,
        detail,
        date: valid,
        diffDays,
        urgency: diffDays === null ? "later" : urgencyOf(diffDays),
        booked: row.booked === true,
        visitOrdinal,
        hasAgreedPlan: row.has_agreed_plan === true,
        deposits: row.deposits ?? [],
        careTeam: row.care_team
          ? {
              doctorId: row.care_team.doctor_id,
              prosthetistId: row.care_team.prosthetist_id,
              doctorName: row.care_team.doctor,
              prosthetistName: row.care_team.prosthetist,
            }
          : null,
      }
    }

    /** The flight check-in, when the server sent one (Radiance 261001). */
    const checkInOf = (
      travelId: string,
      leg: "arrival" | "departure",
      summary: FlightCheckInSummary | undefined,
    ): UnifiedRow["checkIn"] =>
      summary === undefined
        ? undefined
        : {
            travelId,
            leg,
            checkedIn: summary.checked_in,
            opensAt: summary.opens_at ? new Date(summary.opens_at) : null,
          }

    /** The trip's airport ride, in the shape the Driver column dispatches. */
    const rideOf = (
      kind: "arrival_pickup" | "departure_dropoff",
      row: TravelRow,
      date: string | null | undefined,
      time: string | null | undefined,
      drive: DriveSummary | null | undefined,
    ): UnifiedRow["ride"] => {
      const airport = kind === "arrival_pickup" ? row.arrival_airport : row.departure_airport
      return {
        kind,
        travelId: row.travel_id,
        date: date ?? null,
        time: time ?? null,
        // Pickup: airport → hotel. Drop-off: hotel → airport.
        from: (kind === "arrival_pickup" ? airport : row.hotel_name) ?? null,
        to: (kind === "arrival_pickup" ? row.hotel_name : airport) ?? null,
        drive: drive
          ? {
              transferId: drive.transfer_id,
              driverId: drive.driver_id,
              driverName: drive.driver_name,
              pickupDate: drive.pickup_date,
              pickupTime: drive.pickup_time,
            }
          : null,
      }
    }

    /** "TIA · Wizz Air · Grand Hotel" — whichever halves the trip carries. */
    const travelDetail = (parts: (string | null | undefined)[]): string =>
      (parts.filter(Boolean) as string[]).join(" · ")

    const out: UnifiedRow[] = []

    for (const row of data.arrivals) {
      // Dentour 260923 — say which visit this is before anything else: a
      // first-timer and a fifth-time regular are different phone calls.
      const ordinal = row.visit_ordinal ?? null
      const visit =
        ordinal === null
          ? null
          : ordinal <= 1
            ? t("frontdeskBooking.visit.newPatient")
            : t("frontdeskBooking.visit.nth", { n: ordinal })
      const arriving = build(
        `arriving:${row.travel_id}`,
        row,
        "arriving",
        travelDetail([visit, row.arrival_airport, row.arrival_airline, row.arrival_time ?? null, row.hotel_name]) ||
          t("frontdeskBooking.detail.arrivalBare"),
        row.arrival_date,
        ordinal,
      )
      // The desk board sends the pickup + the visit half; the coordinator's
      // default payload does not, and the row then behaves as it always did.
      if (row.drive !== undefined) {
        arriving.ride = rideOf("arrival_pickup", row, row.arrival_date, row.arrival_time, row.drive)
        arriving.visitBooked = row.visit_booked === true
      }
      arriving.checkIn = checkInOf(row.travel_id, "arrival", row.flight_check_in)
      arriving.identityOnFile = row.identity_on_file ?? null
      arriving.visitNextDay = landsLate(row.arrival_time)
      out.push(arriving)
    }

    // Dentour 261008 — landed and still unbooked. Dated by the landing day, so
    // the When column reads how long they have been here without a visit.
    for (const row of data.landed) {
      const flightHome = row.departure_date ? parseLocalDate(row.departure_date) : null
      out.push(
        build(
          `landed:${row.travel_id}`,
          row,
          "landed",
          travelDetail([
            flightHome
              ? t("frontdeskBooking.detail.fliesHome", { date: shortDate(flightHome) })
              : t("frontdeskBooking.detail.noReturn"),
            row.departure_time ?? null,
            row.hotel_name,
          ]),
          row.arrival_date,
        ),
      )
    }

    for (const row of data.departures) {
      // Every departure is listed now; what used to decide WHO appears (open
      // lab work, a pending step before the flight) is kept as a warning.
      const warnings = [
        row.open_lab_work ? t("frontdeskBooking.detail.openLab") : null,
        row.pending_stage ? t("frontdeskBooking.detail.pendingStage") : null,
      ]
      const flight = [row.departure_airport, row.departure_airline, row.departure_time ?? null, row.hotel_name]
      // Without drives (no `drive` key) the row says WHY it is here first, so
      // a truncated detail cell still shows it (261008 audit).
      const visitOnly = row.drive === undefined
      const built = build(
        `departing:${row.travel_id}`,
        row,
        "departing",
        travelDetail(
          visitOnly
            ? [row.booked ? null : t("frontdeskBooking.detail.nothingBooked"), ...warnings, ...flight]
            : [...flight, ...warnings],
        ) || "—",
        row.departure_date,
      )
      // No `drive` key (booking_board_hide_drives_enabled): the server sent
      // the coordinator's visit rule instead, so the row books a visit.
      if (row.drive !== undefined) {
        built.ride = rideOf("departure_dropoff", row, row.departure_date, row.departure_time, row.drive)
      }
      built.checkIn = checkInOf(row.travel_id, "departure", row.flight_check_in)
      built.identityOnFile = row.identity_on_file ?? null
      out.push(built)
    }

    for (const row of data.stage_bookings) {
      const built = build(
        `stage:${row.stage_id}`,
        row,
        "stage",
        t("frontdeskBooking.detail.stage", {
          treatment: row.treatment_title || "—",
          step: row.stage_name,
        }),
        row.planned_date,
      )
      built.stageId = row.stage_id
      built.stageDoctorId = row.treatment_doctor_id ?? null
      out.push(built)
    }

    for (const row of data.undated_stages) {
      const built = build(
        `undated:${row.stage_id}`,
        row,
        "undated",
        t("frontdeskBooking.detail.undated", {
          treatment: row.treatment_title || "—",
          step: row.stage_name,
        }),
        null,
      )
      built.stageId = row.stage_id
      built.stageDoctorId = row.treatment_doctor_id ?? null
      out.push(built)
    }

    for (const row of data.missed_visits) {
      out.push(
        build(
          `missed:${row.appointment_id}`,
          row,
          "missed",
          row.status === "no_show"
            ? t("frontdeskBooking.detail.noShow")
            : t("frontdeskBooking.detail.cancelled"),
          row.missed_at,
        ),
      )
    }

    // "Zirconia · LAB-0003", or "Zirconia + Metal-ceramic · LAB-0003" when the
    // order carries several materials (ArtDent 261007).
    const labHead = (row: { material: string | null; materials?: string[]; order_number: string }) => {
      const names = (row.materials && row.materials.length > 0 ? row.materials : row.material ? [row.material] : [])
        .map((m) => MATERIAL_KEY[m])
        .filter((key): key is NonNullable<typeof key> => key !== undefined)
        .map((key) => tLab(key))
      return names.length > 0 ? `${names.join(" + ")} · ${row.order_number}` : row.order_number
    }

    // Radiance 261005 — no flight yet, so no date: these sink below the dated
    // rows, and the row's action is Add trip.
    for (const row of data.no_trip) {
      const since = row.since ? parseLocalDate(row.since) : null
      out.push(
        build(
          `no_trip:${row.lead_id}`,
          row,
          "no_trip",
          row.joined_by === "deposit"
            ? t("frontdeskBooking.detail.noTripDeposit", { date: since ? shortDate(since) : "—" })
            : t("frontdeskBooking.detail.noTripQuote", { date: since ? shortDate(since) : "—" }),
          null,
        ),
      )
    }

    for (const row of data.delivered_labs) {
      const head = labHead(row)
      out.push(
        build(
          `delivered:${row.lab_order_id}`,
          row,
          "delivered",
          `${head} · ${t("frontdeskBooking.detail.delivered")}`,
          row.delivered_at,
        ),
      )
    }

    for (const row of data.lab_bookings) {
      const head = labHead(row)
      // ArtDent 261007 — a resin try-in waits on a visit, not on the lab.
      if (row.phase.tryin) {
        out.push(
          build(
            `lab:${row.lab_order_id}`,
            row,
            "lab",
            `${head} · ${t("frontdeskBooking.detail.labTryin", { round: row.phase.round ?? 1 })}`,
            row.act_by,
          ),
        )
        continue
      }
      // Name the phase AND the estimate it is measured from — the row's own
      // date is 24h earlier, so without the estimate "act by Thursday" looks
      // arbitrary.
      const estimate = row.phase.due_time
        ? `${row.phase.due_date ?? "—"} ${row.phase.due_time}`
        : (row.phase.due_date ?? "—")
      const detail =
        row.phase.index <= 1
          ? `${head} · ${t("frontdeskBooking.detail.labFirstPhase", {
              phase: row.phase.name,
              total: row.phase.total,
              estimate,
            })}`
          : `${head} · ${t("frontdeskBooking.detail.labPhase", {
              phase: row.phase.name,
              index: row.phase.index,
              total: row.phase.total,
              estimate,
            })}`
      out.push(build(`lab:${row.lab_order_id}`, row, "lab", detail, row.act_by))
    }

    for (const row of data.invoice_bookings) {
      out.push(
        build(
          `invoice:${row.invoice_id}`,
          row,
          "invoice",
          t("frontdeskBooking.detail.invoice", {
            number: row.invoice_number ?? "—",
            status: row.status ?? "—",
          }),
          row.dated,
        ),
      )
    }

    for (const row of data.appointment_tasks) {
      out.push(
        build(
          `task:${row.task_id}`,
          row,
          "task",
          row.requested_by
            ? t("frontdeskBooking.detail.taskBy", {
                title: row.title,
                name: row.requested_by,
              })
            : row.title,
          row.due_at ?? row.requested_at,
        ),
      )
    }

    // ArtDent 261015 — the doctors' follow-up requests, as Doctor rows: the
    // note (or a plain "asks for a visit") and who asked, dated by the target
    // date when the doctor gave one, else by the request itself.
    for (const row of data.followups) {
      const title = row.note || t("frontdeskBooking.detail.followupBare")
      const built = build(
        `followup:${row.followup_id}`,
        row,
        "task",
        row.requested_by
          ? t("frontdeskBooking.detail.taskBy", { title, name: row.requested_by })
          : title,
        row.target_date ?? row.requested_at,
      )
      built.followupId = row.followup_id
      out.push(built)
    }

    // Soonest first; undated rows sink to the bottom rather than float to the top.
    return out.sort((a, b) => {
      if (a.date && b.date) return a.date.getTime() - b.date.getTime()
      if (a.date) return -1
      if (b.date) return 1
      return a.patientName.localeCompare(b.patientName)
    })
  }, [data, t, tLab, shortDate])

  /**
   * Booking status is applied BEFORE the reason counts are taken, so the chips
   * always describe what is actually on screen. Counting the unfiltered set
   * would show "Arriving 12" above a table holding three rows.
   */
  const scoped = useMemo(() => {
    // The logistics desk filters by what a trip still owes, not by booking.
    if (checkInMode) return rows.filter((r) => matchesNeed(r, need))
    if (booking === "booked") return rows.filter((r) => r.booked)
    if (booking === "pending") return rows.filter((r) => !r.booked)
    return rows
  }, [rows, booking, checkInMode, need])

  /** Per-need counts for the logistics desk's status filter, within the chosen reason. */
  const needCounts = useMemo(() => {
    const base = reason === "all" ? rows : rows.filter((r) => r.reason === reason)
    const map = new Map<Need, number>()
    for (const option of NEED_FILTERS) {
      map.set(option.key, base.filter((r) => matchesNeed(r, option.key)).length)
    }
    return map
  }, [rows, reason])

  /** Live per-reason counts — these are what the filter chips display. */
  const counts = useMemo(() => {
    const map = new Map<Reason, number>()
    for (const row of scoped) map.set(row.reason, (map.get(row.reason) ?? 0) + 1)
    return map
  }, [scoped])

  const visible = useMemo(
    () => (reason === "all" ? scoped : scoped.filter((r) => r.reason === reason)),
    [scoped, reason],
  )


  const refresh = useCallback(() => setReloadKey((k) => k + 1), [])

  /** What the row's Book action calls once the appointment is saved. */
  const appointmentsChanged = onAppointmentsChanged ?? refresh

  /**
   * Whether to render the care-team column. Derived from the PAYLOAD, not from
   * the flag: the server drops the block when the clinic has not opted in, so
   * the board follows what it was actually served rather than second-guessing
   * it from a separate flag read.
   */
  const showCareTeam = useMemo(() => rows.some((r) => r.careTeam !== null), [rows])

  /** Driver column (260923): shown whenever the board carries airport rides. */
  const showDrivers = useMemo(() => rows.some((r) => r.ride !== undefined), [rows])

  /**
   * Radiance 261001 — whether the board knows about IDs: the server sends
   * `identity_on_file` only where the clinic tracks it, so the "ID missing"
   * marker and the "Needs ID" filter follow the payload, like the drivers.
   */
  const showIdentity = useMemo(
    () => rows.some((r) => r.identityOnFile !== null && r.identityOnFile !== undefined),
    [rows],
  )

  /** Radiance 261005 — the legend explains the Deposit label while one is on screen. */
  const showDeposits = useMemo(() => rows.some((r) => r.deposits.length > 0), [rows])

  /** …and the late-arrival "Visit tomorrow" note, likewise. */
  const showLateArrivals = useMemo(() => rows.some((r) => r.visitNextDay === true), [rows])

  /**
   * Whether arrival planes are coloured by visit number — only when the server
   * numbered them (visit categorization on), so the legend explains the colours
   * only where they mean something.
   */
  const hasVisitOrdinals = useMemo(() => rows.some((r) => r.visitOrdinal !== null), [rows])

  /**
   * The reasons this board can show, for the Why filter and the legend.
   * Dentour 261008: the server sends no step rows under the flag, so neither
   * offers "Treatment step" or "Needs a date".
   */
  const hideSteps =
    useAuthStore((s) => s.user?.feature_flags?.booking_board_hide_steps_enabled) ?? false
  // ArtDent 261015: the same for "Lab delivered".
  const hideDelivered =
    useAuthStore((s) => s.user?.feature_flags?.booking_board_hide_delivered_enabled) ?? false
  // Dentour 261008: "Landed" exists only where the server sends it.
  const showLanded =
    useAuthStore((s) => s.user?.feature_flags?.booking_board_landed_enabled) ?? false
  const reasons = useMemo(
    () =>
      serverReasons.filter(
        (r) =>
          !(hideSteps && (r === "stage" || r === "undated")) &&
          !(hideDelivered && r === "delivered") &&
          !(r === "landed" && !showLanded),
      ),
    [serverReasons, hideSteps, hideDelivered, showLanded],
  )

  /** Dentour 261008: Book as a full "Book appointment" button, not the icon. */
  const bookButton =
    useAuthStore((s) => s.user?.feature_flags?.booking_board_book_button_enabled) ?? false

  return {
    allowed,
    bookButton,
    showCareTeam,
    showDrivers,
    showIdentity,
    showDeposits,
    showLateArrivals,
    hasVisitOrdinals,
    isLoading,
    days,
    setDays,
    /** The custom window (ArtDent 261015) — see {@link DateWindow}; null on a preset. */
    range,
    setRange,
    /** The window as text for the trigger and the header hint; null on a preset. */
    rangeLabel,
    reason,
    setReason,
    /** The reasons this caller's board carries — iterate these, not REASON_ORDER. */
    reasons,
    /** Flight check-in instead of Book on every row (the logistics desk). */
    checkInMode,
    /** The logistics desk's status filter — see {@link NEED_FILTERS}. */
    need,
    setNeed,
    needCounts,
    /** Every row in the window, before any filter — the logistics header counts these. */
    rows,
    booking,
    setBooking,
    counts,
    visible,
    scoped,
    shortDate,
    refresh,
    appointmentsChanged,
  }
}
